// Frame 11's DOCUMENT COVERAGE card (33:2569).
//
// This is the panel that renders TWO of the three columns Wave 1 backfilled —
// `median_session_length_ms` and `bounced_share`. Both now carry real numbers
// on the seeded corpus, so a "not measured" here would be the regression
// US-110 exists to catch, not a graceful degradation.
//
// Every label on this card is per-VISIT. `unique_sessions` counts visits and
// never people; `median_session_length_ms` is the length of one visit and can
// never be "how long a reader spends with us", because that question needs a
// returning-reader key mAiSight deliberately does not have. The wording is
// locked in metricLabels.ts.

import { Badge, Card, EmptyState, MeterBar } from "../../ui";
import type { DocMetric } from "../../dashboard/api";
import { formatCount, formatDuration, formatShare, medianOf, shownOf } from "./format";
import {
  ENGAGED_LABEL,
  ENGAGED_WHY,
  MEDIAN_VISIT_LABEL,
  SESSIONS_ARE_VISITS,
  SESSIONS_LABEL,
} from "./metricLabels";
import "./analytics.css";

const MAX_TILES = 8;

export type CoverageRow = {
  doc_id: string;
  sessions: number;
  /** Median of the window's daily medians, not of the raw visits. */
  medianVisitMs: number | null;
  bouncedShare: number | null;
};

/** The card has no time axis, so the per-day timeline collapses to one row per
 *  document: sessions sum, and the two backfilled columns take the median of
 *  their daily values so a single quiet day cannot define the document. */
export function collapseTimeline(timeline: DocMetric[]): CoverageRow[] {
  const byDoc = new Map<string, { sessions: number; lengths: (number | null)[]; bounces: (number | null)[] }>();
  for (const day of timeline) {
    const row = byDoc.get(day.doc_id) ?? { sessions: 0, lengths: [], bounces: [] };
    row.sessions += day.unique_sessions;
    row.lengths.push(day.median_session_length_ms);
    row.bounces.push(day.bounced_share);
    byDoc.set(day.doc_id, row);
  }
  return [...byDoc.entries()]
    .map(([doc_id, row]) => ({
      doc_id,
      sessions: row.sessions,
      medianVisitMs: medianOf(row.lengths),
      bouncedShare: medianOf(row.bounces),
    }))
    .sort((a, b) => b.sessions - a.sessions || a.doc_id.localeCompare(b.doc_id));
}

function engagedTone(engaged: number): "nominal" | "flagged" | "danger" {
  if (engaged >= 0.6) return "nominal";
  return engaged >= 0.3 ? "flagged" : "danger";
}

export function DocumentCoverage({ timeline }: { timeline: DocMetric[] }) {
  const rows = collapseTimeline(timeline);
  const shown = rows.slice(0, MAX_TILES);
  const max = shown[0]?.sessions ?? 0;
  const totalSessions = rows.reduce((sum, row) => sum + row.sessions, 0);

  return (
    <Card
      header="Document coverage"
      footer={
        <span className="type-bodyS">
          {`${shownOf(shown.length, rows.length, "document")} · ${formatCount(totalSessions)} ${SESSIONS_LABEL}. ${SESSIONS_ARE_VISITS}`}
        </span>
      }
    >
      {rows.length === 0 ? (
        <EmptyState
          headline="No document traffic in this window"
          body="Coverage needs at least one published document with the widget mounted on it."
        />
      ) : (
        <ul className="maisight-analytics-coverage" aria-label="Document coverage">
          {shown.map((row) => {
            const engaged = row.bouncedShare === null ? null : 1 - row.bouncedShare;
            return (
              <li key={row.doc_id} className="maisight-analytics-coverage-cell">
                <div className="maisight-analytics-coverage-head">
                  <span className="maisight-analytics-term type-numSm">{row.doc_id}</span>
                  {engaged === null ? null : (
                    <span title={ENGAGED_WHY}>
                      <Badge tone={engagedTone(engaged)}>
                        {`${formatShare(engaged)} ${ENGAGED_LABEL}`}
                      </Badge>
                    </span>
                  )}
                </div>
                <MeterBar
                  value={row.sessions}
                  max={max}
                  tone="nominal"
                  label={`${row.doc_id}: ${formatCount(row.sessions)} sessions`}
                />
                <p className="maisight-analytics-coverage-meta type-bodyS">
                  {`${formatCount(row.sessions)} ${SESSIONS_LABEL} · ${formatDuration(row.medianVisitMs)} ${MEDIAN_VISIT_LABEL}`}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
